import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import { Pagination, Response } from '@ui/src';
import { RiskScoreRange } from '../../../types';
import { riskLevelsApi } from '../services';
import ROUTES from '../../../../routes';

export interface RiskLevelsState {
  values?: RiskScoreRange[];
  selectedSettings: string[];
  calculateInProgress?: boolean;
  pagination?: Pagination;
  errorsList?: string;
}

const initialState: RiskLevelsState = {
  values: undefined,
  selectedSettings: [],
  calculateInProgress: false,
};

export const riskLevelsSlice = createSlice({
  name: 'riskLevels',
  initialState,
  reducers: {
    selectSettings: (state, action: PayloadAction<string[]>) => {
      state.selectedSettings = action.payload;
    },
    onCalculate: (state) => {
      state.calculateInProgress = true;
    },
    onCalculateSuccess: (state) => {
      state.calculateInProgress = false;
    },
    clearSettingsListState: (state) => {
      state.values = undefined;
      state.selectedSettings = [];
      state.calculateInProgress = false;
      state.errorsList = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      riskLevelsApi.endpoints.getRiskScoreRanges.matchPending,
      (state) => {
        state.errorsList = undefined;
      },
    );
    builder.addMatcher(
      riskLevelsApi.endpoints.getRiskScoreRanges.matchRejected,
      (state, action: any) => {
        state.values = undefined;
        state.pagination = undefined;

        if (action?.payload.data) {
          if (Array.isArray(action?.payload.data?.message)) {
            state.errorsList = action?.payload.data?.message
              ?.map((item: { value: string }) => item.value)
              .join('; ');
          } else {
            state.errorsList = action?.payload.data?.error;
          }
        }
      },
    );
    builder.addMatcher(
      riskLevelsApi.endpoints.getRiskScoreRanges.matchFulfilled,
      (state, action: PayloadAction<Response<RiskScoreRange>>) => {
        state.values = action.payload.data.edges
          .map((item) => ({
            ...item.node,
            url: generatePath(ROUTES.RISK_LEVELS_EDIT, {
              settingId: item.node.id,
            }),
          }))
          .sort((a, b) => b.rangeEnd - a.rangeEnd);

        state.pagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      riskLevelsApi.endpoints.createUncategorized.matchFulfilled,
      (state, action: PayloadAction<{ data: RiskScoreRange }>) => {
        state.values = [action.payload.data];

        state.pagination = {
          endCursor: action.payload.data.id,
          hasNextPage: false,
          hasPreviousPage: false,
          startCursor: action.payload.data.id,
          totalCount: 1,
        };
      },
    );
    builder.addMatcher(
      riskLevelsApi.endpoints.updateRiskScoreRange.matchFulfilled,
      (state, action: PayloadAction<{ data: RiskScoreRange }>) => {
        const { data } = action.payload;

        state.values = state.values?.map((item) => {
          let result = { ...item };

          if (result.id === data.id) {
            result = {
              ...data,
              url: generatePath(ROUTES.RISK_LEVELS_EDIT, {
                settingId: data.id,
              }),
            };
          }

          return result;
        });
      },
    );
  },
});

export const {
  selectSettings,
  clearSettingsListState,
  onCalculate,
  onCalculateSuccess,
} = riskLevelsSlice.actions;

export default riskLevelsSlice.reducer;
